import React, { useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Card, Slider, Tag } from 'antd';

import { selectors, setAssessmentSlider } from 'state';

import { BUCKET_ORDER } from './constants';

const MARKS = {
  '-5': '-5',
  0: '0',
  5: '+5',
};

export const instrumentsWithDirectConviction = <
  T extends {
    kind: string;
    conviction_allowed?: boolean;
  },
>(
  instruments: T[],
) =>
  instruments.filter(instrument => {
    if (instrument.conviction_allowed !== undefined) {
      return instrument.conviction_allowed;
    }
    // Backward compatibility during a rolling frontend/backend deployment:
    // old APIs did not expose the policy and rejected direct option sliders.
    return instrument.kind !== 'option';
  });

/** Integer conviction slider (-5 strongly bearish … +5 strongly bullish) per
 *  underlying. POSTs on release only (onChangeComplete), optimistic with
 *  rollback in the state module. `sliders` holds current-period values only —
 *  scoring resets to neutral each period, so an unset code renders at 0 with a
 *  "Not set this period" hint until the player restates a conviction. */
const ConvictionSliders: React.FC<{ canWrite: boolean }> = ({ canWrite }) => {
  const dispatch = useDispatch();
  const instruments = useSelector(selectors.assessmentInstruments);
  const sliders = useSelector(selectors.assessmentSliders);

  // Local values while dragging; cleared on release (store takes over).
  const [dragging, setDragging] = useState<{ [code: string]: number }>({});

  const underlyings = useMemo(() => {
    const filtered = instrumentsWithDirectConviction(instruments);
    return [...filtered].sort(
      (a, b) =>
        (BUCKET_ORDER.indexOf(a.code) + 1 || 99) -
        (BUCKET_ORDER.indexOf(b.code) + 1 || 99),
    );
  }, [instruments]);

  return (
    <div className="assessment-sliders-grid">
      {underlyings.map(instrument => {
        const stored = sliders[instrument.code];
        const value = dragging[instrument.code] ?? stored ?? 0;
        const unset =
          stored === undefined && dragging[instrument.code] === undefined;
        return (
          <Card size="small" key={instrument.code}>
            <div className="assessment-slider-card">
              <div className="assessment-slider-label">
                <strong>{instrument.code}</strong>
                {unset ? (
                  <Tag style={{ opacity: 0.65 }}>Not set this period</Tag>
                ) : (
                  <Tag
                    color={value > 0 ? 'green' : value < 0 ? 'red' : 'default'}
                  >
                    {value > 0 ? `+${value}` : value}
                  </Tag>
                )}
              </div>
              <Slider
                min={-5}
                max={5}
                step={1}
                marks={MARKS}
                included={false}
                disabled={!canWrite}
                value={value}
                onChange={val =>
                  setDragging(prev => ({
                    ...prev,
                    [instrument.code]: val as number,
                  }))
                }
                onChangeComplete={val => {
                  setDragging(prev => {
                    const next = { ...prev };
                    delete next[instrument.code];
                    return next;
                  });
                  if ((val as number) === stored) return;
                  dispatch(
                    setAssessmentSlider({
                      instrument_code: instrument.code,
                      value: val as number,
                      prev_value: stored,
                    }),
                  );
                }}
              />
              <div className="assessment-slider-scale">
                <span>Strongly bearish</span>
                <span>Neutral</span>
                <span>Strongly bullish</span>
              </div>
            </div>
          </Card>
        );
      })}
    </div>
  );
};

export default ConvictionSliders;
